import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import { MotiView } from "moti";
import { ArrowLeftIcon } from "@phosphor-icons/react";

import DefaultLayout from "./../layouts/DefaultLayout.web";
import CustomText from "./../components/common/general/CustomText/CustomText";
import CustomButton from "./../components/common/general/CustomButton/CustomButton";
import CustomHtml from "./../components/common/general/CustomHtml/CustomHtml";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { useGetAnnouncementByIdQuery } from "@axio/graphql/generated/graphql";
import { formatDateWithTime } from "./../utils/date";
import type { RootStackParamList } from "../navigation/navigation.types";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";

const AnnouncementDetailView = () => {
  const route = useRoute<RouteProp<RootStackParamList, "Announcement Detail">>();
  const { announcementId } = route.params;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { isMedium } = useResponsiveScreen();

  const { data, loading } = useGetAnnouncementByIdQuery({
    variables: {
      id: announcementId,
    },
    skip: !announcementId,
  });

  const announcement = data?.getAnnouncementById?.data;

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("Announcement");
    }
  };

  return (
    <DefaultLayout pageHasChat={false}>
      <MotiView
        style={{ flex: 1 }}
        from={{ opacity: 0, translateY: 20, scale: 1 }}
        animate={{ opacity: 1, translateY: 0, scale: 1 }}
        delay={500}>
        {loading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        )}
        {!loading && announcement != null && (
          <>
            <CustomButton
              text="Back"
              styleType="transparent"
              onPress={handleBackPress}
              useTranslationText={false}
              style={styles.backButton}
              textStyle={styles.backButtonText}
              leftIcon={<ArrowLeftIcon size={16} color="#64748B" />}
            />
            <CustomSpacing type="vertical" size="l" />

            <View style={styles.headerContainer}>
              <CustomText
                role="heading"
                accessibilityRole="header"
                aria-level={1}
                text={announcement.title ?? ""}
                size={isMedium ? "m" : "xs"}
                textType="display"
                weight="ultraBold"
                useTranslationText={false}
              />
            </View>

            <View>
              <CustomText
                text={formatDateWithTime(announcement.created)}
                size="xs"
                style={styles.createdDate}
                useTranslationText={false}
                role="text"
                aria-label={`Posted on ${formatDateWithTime(announcement.created)}`}
                accessibilityLabel={`Posted on ${formatDateWithTime(announcement.created)}`}
              />
            </View>

            <View style={styles.message}>
              <CustomHtml html={announcement.message} />
            </View>
          </>
        )}
      </MotiView>
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    flexDirection: "column",
    marginBottom: 28,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  backButton: {
    alignSelf: "flex-start",
  },
  backButtonText: {
    color: "#64748B",
    fontSize: 16,
  },
  createdDate: {
    color: "#667085",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 400,
  },
  message: {
    marginTop: 16,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 400,
    color: "#344054",
  },
});

export default AnnouncementDetailView;
